/**
 * NPSPrompt — Day 1-3 trial / Day 25-35 paid NPS survey modal.
 *
 * Auto-polls GET /api/nps/me on mount; if `prompt: true`, surfaces a sticky
 * gold-accent modal with a 0-10 score selector + optional comment. On submit,
 * thanks the user. Detractors (≤6) also trigger an admin escalation email
 * server-side.
 *
 * Dismissible — once dismissed, we set a localStorage flag so we don't bug
 * the user again this session.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Sparkles, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DISMISS_KEY = 'ibclub_nps_dismissed_session';

const NPSPrompt = () => {
  const [show, setShow] = useState(false);
  const [context, setContext] = useState('trial_d7');
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [thanks, setThanks] = useState('');

  useEffect(() => {
    // Skip if dismissed this session
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === '1') return;
    } catch { /* ignore */ }

    axios.get(`${API}/nps/me`, { withCredentials: true })
      .then((r) => {
        if (r.data?.prompt) {
          setShow(true);
          setContext(r.data.context || 'trial_d7');
        }
      })
      .catch(() => {});
  }, []);

  const handleDismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setShow(false);
  };

  const handleSubmit = async () => {
    if (score === null) return;
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/nps/submit`,
        { score, comment: comment.trim(), context },
        { withCredentials: true },
      );
      setThanks(r.data?.thanks_message || 'Thanks for the feedback!');
      setSubmitted(true);
      try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return createPortal(
    <div
      className="fixed bottom-4 right-4 left-4 sm:left-auto sm:max-w-sm z-[80] glass-card border border-[#d4af37]/40 p-5 rounded-xl shadow-2xl"
      data-testid="nps-prompt"
    >
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        data-testid="nps-dismiss"
        className="absolute top-3 right-3 text-slate-500 hover:text-white p-1"
      >
        <X size={14} />
      </button>

      {submitted ? (
        <div className="text-center py-3">
          <Sparkles size={24} className="text-[#d4af37] mx-auto mb-2" />
          <p className="text-white font-semibold mb-1">Got it.</p>
          <p className="text-xs text-slate-400">{thanks}</p>
        </div>
      ) : (
        <>
          <p className="text-[10px] uppercase tracking-widest text-[#d4af37] mb-2">Quick question</p>
          <h3 className="text-white font-semibold text-base mb-3 leading-snug">
            How likely are you to recommend IB Club to a fellow founder/investor?
          </h3>

          {/* 0-10 score buttons */}
          <div className="grid grid-cols-11 gap-1 mb-3">
            {Array.from({ length: 11 }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setScore(i)}
                data-testid={`nps-score-${i}`}
                className={`aspect-square text-xs rounded transition-colors ${
                  score === i
                    ? 'bg-[#d4af37] text-slate-950 font-bold'
                    : 'bg-white/5 text-slate-300 hover:bg-white/10'
                }`}
              >
                {i}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-[9px] text-slate-500 uppercase tracking-wider mb-3">
            <span>Not likely</span>
            <span>Very likely</span>
          </div>

          {score !== null && (
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={
                score <= 6
                  ? "What would make IB Club a 10 for you?"
                  : score <= 8
                    ? "Anything we could improve?"
                    : "What's been the most useful?"
              }
              rows={2}
              maxLength={500}
              data-testid="nps-comment"
              className="w-full bg-slate-950/60 border border-white/10 text-white text-xs px-3 py-2 rounded-md focus:outline-none focus:border-[#d4af37]/60 resize-none mb-3"
            />
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={score === null || submitting}
            data-testid="nps-submit"
            className="w-full bg-[#d4af37] text-slate-950 hover:bg-[#e5c558] disabled:opacity-40 font-semibold text-xs uppercase tracking-wider px-4 py-2.5 inline-flex items-center justify-center gap-2"
          >
            <Send size={12} /> {submitting ? 'Sending…' : 'Submit'}
          </button>
        </>
      )}
    </div>,
    document.body
  );
};

export default NPSPrompt;
